import { create } from 'zustand';

import type { DataCategory, RouteModel } from '../types';

export type PromptVariant = 'paused' | 'running' | 'manual';

export type PromptDecision = 'saved' | 'later' | 'elsewhere';

export interface PromptRequest {
  id: string;
  variant: PromptVariant;
  investorName: string | null;
  categories: DataCategory[];
  selectedModel: RouteModel | null;
}

interface PolicyPromptState {
  request: PromptRequest | null;
  open: (request: Omit<PromptRequest, 'id'>, signal?: AbortSignal) => Promise<PromptDecision>;
  resolve: (decision: PromptDecision) => void;
}

const resolvers = new Map<string, (decision: PromptDecision) => void>();
let seq = 0;

export const usePolicyPromptStore = create<PolicyPromptState>()((set, get) => ({
  request: null,

  open(request, signal) {
    const id = `policy-prompt-${String(++seq)}`;
    const previous = get().request;
    if (previous) resolvers.get(previous.id)?.('later');
    return new Promise<PromptDecision>((resolve) => {
      const settle = (decision: PromptDecision) => {
        resolvers.delete(id);
        if (get().request?.id === id) set({ request: null });
        resolve(decision);
      };
      resolvers.set(id, settle);
      set({ request: { ...request, id } });
      if (signal) {
        if (signal.aborted) {
          settle('later');
          return;
        }
        signal.addEventListener(
          'abort',
          () => {
            settle('later');
          },
          { once: true },
        );
      }
    });
  },

  resolve(decision) {
    const current = get().request;
    if (!current) return;
    const settle = resolvers.get(current.id);
    if (settle) settle(decision);
    else set({ request: null });
  },
}));
